/**
 * Organization Service - API methods for organization management
 */

import apiClient from './apiClient';
import type { BillingCycle } from '@upskill/shared';

// Organization types
export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  mfaEnabled: boolean;
  isActive: boolean;
  status?: 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  contactEmail?: string;
  memberCount?: number;
  billingCycle?: BillingCycle | null;
  timezone?: string | null;
  planId?: string | null;
  plan?: { id: string; name: string; isActive: boolean } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Create organization request (basic)
export interface CreateOrganizationRequest {
  name: string;
  slug: string;
  description?: string;
  mfaEnabled?: boolean;
  timezone?: string | null;
}

// Create organization with admin request
export interface CreateOrganizationWithAdminRequest {
  organization: {
    name: string;
    slug: string;
    description?: string;
    mfaEnabled?: boolean;
    timezone?: string | null;
  };
  admin: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  };
}

// Update organization request
export interface UpdateOrganizationRequest {
  name?: string;
  slug?: string;
  description?: string;
  mfaEnabled?: boolean;
  isActive?: boolean;
  timezone?: string | null;
}

// List organizations params
export interface ListOrganizationsParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

/**
 * Get paginated list of all organizations (Super Admin only)
 */
export const getOrganizations = async (
  params: ListOrganizationsParams = {},
): Promise<PaginatedResponse<Organization>> => {
  const response = await apiClient.get<{ success: boolean } & PaginatedResponse<Organization>>(
    '/api/v1/organizations',
    { params },
  );
  return {
    data: response.data.data,
    meta: response.data.meta,
  };
};

/**
 * Get organization by ID
 */
export const getOrganizationById = async (id: string): Promise<Organization> => {
  const response = await apiClient.get<{ success: boolean; data: Organization }>(
    `/api/v1/organizations/${id}`,
  );
  return response.data.data;
};

/**
 * Get organization by slug
 */
export const getOrganizationBySlug = async (slug: string): Promise<Organization> => {
  const response = await apiClient.get<{ success: boolean; data: Organization }>(
    `/api/v1/organizations/slug/${slug}`,
  );
  return response.data.data;
};

/**
 * Get current user's organization
 */
export const getMyOrganization = async (): Promise<Organization> => {
  const response = await apiClient.get<{ success: boolean; data: Organization }>(
    '/api/v1/organizations/my',
  );
  return response.data.data;
};

/**
 * Create new organization (basic)
 */
export const createOrganization = async (
  data: CreateOrganizationRequest,
): Promise<Organization> => {
  const response = await apiClient.post<{ success: boolean; data: Organization }>(
    '/api/v1/organizations',
    data,
  );
  return response.data.data;
};

/**
 * Create organization with admin user
 */
export const createOrganizationWithAdmin = async (
  data: CreateOrganizationWithAdminRequest,
): Promise<{
  organization: Organization;
  admin: { id: string; email: string; entraProvisioned: boolean };
}> => {
  const response = await apiClient.post<{
    success: boolean;
    data: { organization: Organization; admin: { id: string; email: string; entraProvisioned: boolean } };
  }>('/api/v1/organizations/with-admin', data);
  return response.data.data;
};

/**
 * Update organization
 */
export const updateOrganization = async (
  id: string,
  data: UpdateOrganizationRequest,
): Promise<Organization> => {
  const response = await apiClient.patch<{ success: boolean; data: Organization }>(
    `/api/v1/organizations/${id}`,
    data,
  );
  return response.data.data;
};

/**
 * Update current user's organization
 */
export const updateMyOrganization = async (
  data: UpdateOrganizationRequest,
): Promise<Organization> => {
  const response = await apiClient.patch<{ success: boolean; data: Organization }>(
    '/api/v1/organizations/my',
    data,
  );
  return response.data.data;
};

/**
 * Deactivate organization (soft delete)
 */
export const deactivateOrganization = async (id: string): Promise<{ message: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string }>(
    `/api/v1/organizations/${id}/deactivate`,
  );
  return { message: response.data.message };
};

// ============================================================================
// Org Settings (Security / Policy)
// ============================================================================

export interface OrgSettings {
  sessionTimeoutMinutes: number;
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireNumber: boolean;
  passwordRequireSpecial: boolean;
  invitationExpirationDays: number;
}

/**
 * Get security/policy settings for the current organization
 */
export const getOrgSettings = async (): Promise<OrgSettings> => {
  const response = await apiClient.get<{ success: boolean; data: OrgSettings }>(
    '/api/v1/organizations/my/settings',
  );
  return response.data.data;
};

/**
 * Update security/policy settings for the current organization
 */
export const updateOrgSettings = async (data: Partial<OrgSettings>): Promise<OrgSettings> => {
  const response = await apiClient.patch<{ success: boolean; data: OrgSettings }>(
    '/api/v1/organizations/my/settings',
    data,
  );
  return response.data.data;
};

// ============================================================================
// SLA Config
// ============================================================================

export type SlaTier = 'NORMAL' | 'WARNING' | 'ESCALATED';

export interface SlaConfig {
  greenThreshold: number;
  yellowThreshold: number;
  forcedAckEnabled: boolean;
  forcedAckTier: SlaTier;
  emailNotificationTier: SlaTier;
  holdAutoReleaseHours: number;
  caseloadReviewDueHours: number;
  repeatedBreachThreshold: number;
  repeatedBreachWindowDays: number;
  useBusinessDays: boolean;
  /** Calendar days a Red Dot has to be resolved, measured from submission. Default 14. */
  redDotSlaDays: number;
  maxTasksPerUser: number;
}

/**
 * Get SLA configuration for the current organization
 */
export const getSlaConfig = async (): Promise<SlaConfig> => {
  const response = await apiClient.get<{ success: boolean; data: SlaConfig }>(
    '/api/v1/organizations/my/sla-config',
  );
  return response.data.data;
};

/**
 * Update SLA configuration for the current organization
 */
export const updateSlaConfig = async (data: Partial<SlaConfig>): Promise<SlaConfig> => {
  const response = await apiClient.patch<{ success: boolean; data: SlaConfig }>(
    '/api/v1/organizations/my/sla-config',
    data,
  );
  return response.data.data;
};

// ============================================================================
// Notification Preferences
// ============================================================================

export interface NotificationPreferences {
  emailOnApproved: boolean;
  emailOnReport: boolean;
  emailOnNewMember: boolean;
}

/**
 * Get notification preferences for the current organization
 */
export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const response = await apiClient.get<{ success: boolean; data: NotificationPreferences }>(
    '/api/v1/organizations/my/notification-preferences',
  );
  return response.data.data;
};

/**
 * Update notification preferences for the current organization
 */
export const updateNotificationPreferences = async (
  data: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> => {
  const response = await apiClient.patch<{ success: boolean; data: NotificationPreferences }>(
    '/api/v1/organizations/my/notification-preferences',
    data,
  );
  return response.data.data;
};

const organizationService = {
  getOrganizations,
  getOrganizationById,
  getOrganizationBySlug,
  getMyOrganization,
  createOrganization,
  createOrganizationWithAdmin,
  updateOrganization,
  updateMyOrganization,
  deactivateOrganization,
  getOrgSettings,
  updateOrgSettings,
  getSlaConfig,
  updateSlaConfig,
  getNotificationPreferences,
  updateNotificationPreferences,
};

export default organizationService;
